import { memo, useCallback, useMemo } from 'react';
import type { Product } from '../../types/tycoon';
import { formatCurrency } from '../../utils/currencyFormatter';
import { formatCompactNumber } from '../../utils/numberFormatter';
import { GAME_FORMULAS } from '../../config/gameFormulas';
import { GAME_CONSTANTS } from '../../config/gameConstants';
import { useGameStore } from '../../stores/gameStore';
import { useProductionStore } from '../../stores/productionStore';
import { useInventoryStore } from '../../stores/inventoryStore';

// Stable empty reference to avoid infinite re-renders in selectors
const EMPTY_MAP = new Map();

interface ProductCardProps {
  product: Product;
  onSelect?: (id: string) => void;
}

export const ProductCard = memo(({ product, onSelect }: ProductCardProps) => {
  // Status phase detection (computed first to optimize store subscriptions)
  const isDevPhase = ['concept', 'design', 'engineering', 'testing'].includes(product.status);
  const isManufacturingPhase = product.status === 'manufacturing';
  const isReleasePhase = ['production', 'released', 'discontinued'].includes(product.status);

  // Only subscribe to stores when needed for the current phase
  const gameDate = useGameStore(state => state.gameDate);
  const productionLines = useProductionStore(state =>
    isManufacturingPhase ? state.productionLines : EMPTY_MAP
  );
  const devices = useInventoryStore(state =>
    isManufacturingPhase ? state.devices : EMPTY_MAP
  );

  // Calculate margin
  const margin = GAME_FORMULAS.finance.getProfitMargin(product.price, product.productionCost);

  // Status badge styling
  const getStatusStyle = () => {
    switch (product.status) {
      case 'production':
        return 'bg-theme-warning/20 text-theme-warning';
      case 'released':
        return 'bg-theme-success/20 text-theme-success';
      case 'testing':
      case 'engineering':
      case 'design':
      case 'concept':
        return 'bg-theme-info/20 text-theme-info';
      case 'manufacturing':
        return 'bg-theme-warning/20 text-theme-warning';
      case 'discontinued':
        return 'bg-theme-error/20 text-theme-error';
      default:
        return 'bg-theme-bg-tertiary text-theme-text-primary';
    }
  };

  const getStatusLabel = () => {
    switch (product.status) {
      case 'production': return 'Production';
      case 'released': return 'Released';
      case 'testing': return 'Testing';
      case 'engineering': return 'Engineering';
      case 'design': return 'Design';
      case 'concept': return 'Concept';
      case 'manufacturing': return 'Manufacturing';
      case 'discontinued': return 'Discontinued';
      default: return product.status;
    }
  };

  // Manufacturing phase data (memoized to prevent recalculation on every render)
  const manufacturingData = useMemo(() => {
    if (!isManufacturingPhase) {
      return { lineCount: 0, monthlyOutput: 0, unitsInStock: 0 };
    }
    const lines = Array.from(productionLines.values()).filter(
      line => line.productId === product.id
    );
    const lineCount = lines.length;
    const dailyOutput = lines.reduce((sum, line) => sum + (line.dailyOutput || 0), 0);
    const monthlyOutput = dailyOutput * GAME_CONSTANTS.time.daysPerMonth;
    const inventory = devices.get(product.id);
    const unitsInStock = inventory?.currentStock ?? 0;

    return { lineCount, monthlyOutput, unitsInStock };
  }, [isManufacturingPhase, productionLines, devices, product.id]);

  // Development phase data (memoized to prevent recalculation on every render)
  const devData = useMemo(() => {
    const phase = product.developmentPhase;
    if (!phase) {
      return { completion: 0, daysRemaining: 0, confidence: 0 };
    }

    // Use shared utility for date calculations
    const daysSince = GAME_FORMULAS.time.getDaysBetween(phase.startDate, gameDate);
    // Guard against division by zero
    const totalDays = phase.estimatedDuration || 1;
    const completion = Math.min(100, Math.round((daysSince / totalDays) * 100));
    const daysRemaining = Math.max(0, totalDays - daysSince);

    return {
      completion,
      daysRemaining,
      confidence: phase.confidence || 0,
    };
  }, [product.developmentPhase, gameDate]);

  // Release phase data
  const unitsSold = product.salesMetrics?.unitsSold || 0;
  const revenue = product.salesMetrics?.revenue || 0;
  const marketShare = product.salesMetrics?.marketShare || 0;
  const avgRating = product.salesMetrics?.averageRating || 0;
  const reviewCount = product.reviews?.customer?.length || 0;

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect?.(product.id);
    }
  }, [onSelect, product.id]);

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`${product.name} - ${getStatusLabel()} - ${formatCurrency(product.price)}`}
      className="bg-theme-bg-content border border-theme-border flex flex-col hover:border-theme-border transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      onClick={() => onSelect?.(product.id)}
      onKeyDown={handleKeyDown}
    >
      {/* Header */}
      <div className="bg-theme-bg-secondary px-4 py-3 border-b border-theme-border flex justify-between items-center">
        <h3 className="text-sm font-semibold text-theme-text-primary truncate">{product.name}</h3>
        <span className={`px-2 py-1 text-xs font-medium uppercase ${getStatusStyle()}`}>
          {getStatusLabel()}
        </span>
      </div>

      {/* Body */}
      <div className="p-4">
        {/* Main Product Info with Phone Preview */}
        <div className="flex gap-4 mb-4">
          {/* Simple Phone Mockup */}
          <div
            className="w-14 h-24 flex-shrink-0 rounded-lg border-2 relative"
            style={{
              borderColor: product.designConfig?.color || '#9ca3af',
              background: `linear-gradient(135deg, ${product.designConfig?.color || '#374151'} 0%, ${product.designConfig?.design?.buttonColor || '#1f2937'} 100%)`,
            }}
          >
            {/* Screen area */}
            <div className="absolute inset-1.5 bg-gray-900 rounded-md" />
            {/* Top notch/speaker */}
            <div className="absolute top-2.5 left-1/2 -translate-x-1/2 w-5 h-1 bg-gray-700 rounded-sm" />
          </div>

          {/* Specs */}
          <div className="flex-1 space-y-1.5">
            <div className="flex justify-between text-xs">
              <span className="text-theme-text-secondary font-medium">Price</span>
              <span className="text-theme-text-primary font-semibold">{formatCurrency(product.price)}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-theme-text-secondary font-medium">Cost</span>
              <span className="text-theme-text-primary font-semibold">{formatCurrency(product.productionCost)}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-theme-text-secondary font-medium">Margin</span>
              <span className="text-theme-text-primary font-semibold">{margin.toFixed(1)}%</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-theme-text-secondary font-medium">Tech Score</span>
              <span className="text-theme-text-primary font-semibold">{Math.round(product.techScore)}/100</span>
            </div>
          </div>
        </div>

        {/* Metrics Grid - varies by phase */}
        {isReleasePhase && (
          // Released/Discontinued Product Metrics
          <div className="grid grid-cols-2 gap-3 pt-3 border-t border-theme-border">
            <div className="flex flex-col gap-0.5">
              <div className="text-xs uppercase text-theme-text-secondary font-semibold tracking-wide">Units Sold</div>
              <div className="text-lg font-bold text-theme-text-primary">{formatCompactNumber(unitsSold)}</div>
              <div className="text-xs text-theme-text-muted">Lifetime</div>
            </div>
            <div className="flex flex-col gap-0.5">
              <div className="text-xs uppercase text-theme-text-secondary font-semibold tracking-wide">Revenue</div>
              <div className="text-lg font-bold text-theme-success">{formatCurrency(revenue)}</div>
              <div className="text-xs text-theme-text-muted">Total</div>
            </div>
            <div className="flex flex-col gap-0.5">
              <div className="text-xs uppercase text-theme-text-secondary font-semibold tracking-wide">Market Share</div>
              <div className="text-lg font-bold text-theme-text-primary">{(marketShare * 100).toFixed(1)}%</div>
              <div className="text-xs text-theme-text-muted">Current</div>
            </div>
            <div className="flex flex-col gap-0.5">
              <div className="text-xs uppercase text-theme-text-secondary font-semibold tracking-wide">Avg Rating</div>
              <div className="text-lg font-bold text-theme-text-primary">
                {avgRating === 0 ? 'Not yet rated' : `${avgRating.toFixed(1)}/5`}
              </div>
              <div className="text-xs text-theme-text-muted">{formatCompactNumber(reviewCount)} reviews</div>
            </div>
          </div>
        )}

        {isManufacturingPhase && (
          <div className="grid grid-cols-2 gap-3 pt-3 border-t border-theme-border">
            <div className="flex flex-col gap-0.5">
              <div className="text-xs uppercase text-theme-text-secondary font-semibold tracking-wide">Factory Lines</div>
              <div className="text-lg font-bold text-theme-text-primary">{manufacturingData.lineCount}</div>
              <div className="text-xs text-theme-text-muted">Assigned</div>
            </div>
            <div className="flex flex-col gap-0.5">
              <div className="text-xs uppercase text-theme-text-secondary font-semibold tracking-wide">In Inventory</div>
              <div className="text-lg font-bold text-theme-info">{formatCompactNumber(manufacturingData.unitsInStock)}</div>
              <div className="text-xs text-theme-text-muted">Units</div>
            </div>
            <div className="flex flex-col gap-0.5">
              <div className="text-xs uppercase text-theme-text-secondary font-semibold tracking-wide">Output/Month</div>
              <div className="text-lg font-bold text-theme-text-primary">{formatCompactNumber(manufacturingData.monthlyOutput)}</div>
              <div className="text-xs text-theme-text-muted">Units</div>
            </div>
            <div className="flex flex-col gap-0.5">
              <div className="text-xs uppercase text-theme-text-secondary font-semibold tracking-wide">Quality</div>
              <div className="text-lg font-bold text-theme-text-primary">{Math.round(product.developmentPhase?.qualityScore || 0)}%</div>
              <div className="text-xs text-theme-text-muted">Score</div>
            </div>
          </div>
        )}

        {isDevPhase && (
          <div className="grid grid-cols-2 gap-3 pt-3 border-t border-theme-border">
            <div className="flex flex-col gap-0.5">
              <div className="text-xs uppercase text-theme-text-secondary font-semibold tracking-wide">Completion</div>
              <div className="text-lg font-bold text-theme-text-primary">{devData.completion}%</div>
              <div className="text-xs text-theme-text-muted">{devData.daysRemaining} days left</div>
            </div>
            <div className="flex flex-col gap-0.5">
              <div className="text-xs uppercase text-theme-text-secondary font-semibold tracking-wide">Dev Cost</div>
              <div className="text-lg font-bold text-theme-error">{formatCurrency(product.developmentCost || 0)}</div>
              <div className="text-xs text-theme-text-muted">Total spent</div>
            </div>
            <div className="flex flex-col gap-0.5">
              <div className="text-xs uppercase text-theme-text-secondary font-semibold tracking-wide">Confidence</div>
              <div className="text-lg font-bold text-theme-text-primary">{devData.confidence}%</div>
              <div className="text-xs text-theme-text-muted">Success rate</div>
            </div>
            <div className="flex flex-col gap-0.5">
              <div className="text-xs uppercase text-theme-text-secondary font-semibold tracking-wide">Quality</div>
              <div className="text-lg font-bold text-theme-text-primary">{Math.round(product.developmentPhase?.qualityScore || 0)}%</div>
              <div className="text-xs text-theme-text-muted">Target</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

ProductCard.displayName = 'ProductCard';
